/**
 * Workbooks the Copilot builds in a thread.
 *
 * Unlike a download offer, which points at a table still living in the
 * session's DuckDB, a workbook is stored: the bytes sit in Postgres, so a file
 * built last week still downloads today and survives a reopened thread. That
 * is why this carries an id of its own rather than a frame pointer.
 *
 * A workbook is also versioned. "Add a sheet" writes a new version and leaves
 * the previous file untouched, so something already shared stays findable.
 */

export interface WorkbookSheet {
  name: string;
  rowCount: number;
}

export interface Workbook {
  /** Stable per workbook version, so the same card survives a reload. */
  id: string;
  workbookId: string;
  title: string;
  filename: string;
  /** 1 for the first build, then one per edit. Shown as "v3". */
  version: number;
  sheets: WorkbookSheet[];
  sizeBytes: number;
}

/** Map a `workbook_ready` event payload, or a history row, onto the client shape. */
export function toWorkbook(raw: any): Workbook {
  const workbookId = String(raw?.workbook_id || raw?.workbookId || '');
  return {
    id: String(raw?.id || workbookId),
    workbookId,
    title: String(raw?.title || 'Workbook'),
    filename: String(raw?.filename || 'workbook.xlsx'),
    version: Number(raw?.version ?? 1),
    sheets: (raw?.sheets || [])
      .map((s: any) => ({
        name: String(s?.name || ''),
        rowCount: Number(s?.row_count ?? s?.rowCount ?? 0),
      }))
      .filter((s: WorkbookSheet) => s.name),
    sizeBytes: Number(raw?.size_bytes ?? raw?.sizeBytes ?? 0),
  };
}
